import { FormState, FORM_ERROR } from "final-form"
import { Field, Form, FormRenderProps, FormSpy } from "react-final-form"
import classNames from "utils/preact/class-names"

import { checkEmail, checkPassword } from "../services/validation"
import { getCsrfToken } from "../services/backend"
import { Screens } from "../services/screens"
import { profile } from "../services/store"
import { useTranslation } from "../components/Translation"
import { SubscriptionButtons } from "../components/SubscriptionButtons"
import css from "../style.module.scss"
import { Sidebar } from "../components/Sidebar"

interface FormValues {
  email: string
  password: string
}

export function SignIn({
  token,
  onAuthenticate,
  onPasswordForgot,
  onPageUpdate,
}: {
  token: string
  onAuthenticate: () => void
  onPasswordForgot: () => void
  onPageUpdate: (page: Screens) => void
}) {
  const t = useTranslation()

  return (
    <div className={css.content}>
      <div className={css["split-container"]}>
        <div className={css["flex-grow"]}>
          <Sidebar />
          <SubscriptionButtons />
        </div>
        <div className={css["horizontal-divider"]} />
        <div className={css["flex-grow"]}>
          <h3 className={classNames({ [css["form-title"]]: true, [css["mb-2"]]: true })}>
            {t("log_in")}
          </h3>

          {/* Apple Sign In Section
          <AppleSignInButton />

          <div className={css['login-divider']}>
            <hr />
            <span>or</span>
            <hr />
          </div> */}

          <Form
            onSubmit={(values: FormValues): Promise<Object | undefined> =>
              fetch("/session", {
                method: "post",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  "X-CSRF-Token": getCsrfToken(token),
                },
                credentials: "include",
                body: JSON.stringify(values),
              })
                .then((res) => {
                  if (!res.ok) {
                    if (res.status !== 422 && res.status !== 401) {
                      return {
                        [FORM_ERROR]: `${res.status}: ${res.statusText}`,
                      }
                    }
                  }

                  return res.json()
                })
                .then((res) => {
                  if (res.user) {
                    profile.setKey("emailVerified", res.user.emailVerified)
                    profile.setKey("premium", res.user.premium)
                    profile.setKey("email", res.user.email)
                  }

                  if (res.success) return undefined
                  if (res.errors) return res.errors
                  return res
                })
                .catch((err: Error) => ({
                  [FORM_ERROR]: err.message,
                }))
            }
          >
            {({
              hasValidationErrors,
              hasSubmitErrors,
              submitErrors,
              submitError,
              submitting,
              pristine,
              handleSubmit,
            }: FormRenderProps & FormState<FormValues>) => (
              <form action="/session" method="post" onSubmit={handleSubmit}>
                <FormSpy
                  subscription={{ submitSucceeded: true }}
                  onChange={({
                    submitSucceeded,
                  }: {
                    values: FormValues
                    submitSucceeded: boolean
                  }) => {
                    if (submitSucceeded) {
                      profile.setKey("authorized", true)
                      profile.setKey("reloadRequired", true)
                      onAuthenticate()
                    }
                  }}
                />
                <Field
                  name="email"
                  validate={(value) => checkEmail(t, value)}
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css["is-valid"]]: meta.touched && meta.valid,
                        [css["is-invalid"]]: meta.touched && meta.invalid,
                      })}
                      htmlFor="email"
                    >
                      {t("email")}
                      <span className={css.error}>
                        {((meta.touched && meta.error) || meta.submitError) &&
                          `(${(meta.touched && meta.error) || meta.submitError})`}
                      </span>
                      <div
                        className={classNames({
                          [css["input-wrapper"]]: true,
                          [css["is-valid"]]: meta.touched && meta.valid,
                          [css["is-invalid"]]: meta.touched && meta.invalid,
                        })}
                      >
                        <input
                          {...input}
                          id="email"
                          type="email"
                          autoComplete="email"
                          className={classNames({
                            [css.input]: true,
                            [css["is-valid"]]: meta.touched && meta.valid,
                            [css["is-invalid"]]: meta.touched && meta.invalid,
                          })}
                        />
                      </div>
                    </label>
                  )}
                />
                <Field
                  name="password"
                  validate={(value) => checkPassword(t, value)}
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css["is-valid"]]: meta.touched && meta.valid,
                        [css["is-invalid"]]: meta.touched && meta.invalid,
                      })}
                      htmlFor="password"
                    >
                      {t("password")}
                      <span className={css.error}>
                        {((meta.touched && meta.error) || meta.submitError) &&
                          `(${(meta.touched && meta.error) || meta.submitError})`}
                      </span>
                      <div
                        className={classNames({
                          [css["input-wrapper"]]: true,
                          [css["is-valid"]]: meta.touched && meta.valid,
                          [css["is-invalid"]]: meta.touched && meta.invalid,
                        })}
                      >
                        <input
                          {...input}
                          id="password"
                          type="password"
                          autoComplete="new-password"
                          className={classNames({
                            [css.input]: true,
                            [css["is-valid"]]: meta.touched && meta.valid,
                            [css["is-invalid"]]: meta.touched && meta.invalid,
                          })}
                        />
                      </div>
                      <a
                        className={classNames({
                          [css["text-highlight"]]: true,
                          [css["font-regular"]]: true,
                          [css["text-underline"]]: true,
                        })}
                        href="/passwords/new"
                        onClick={(e) => {
                          e.preventDefault()
                          onPasswordForgot()
                        }}
                      >
                        {t("forgot_password")}
                      </a>
                    </label>
                  )}
                />

                {hasSubmitErrors && (
                  <p
                    className={classNames({
                      [css["text-medium"]]: true,
                      [css["error-highlight"]]: true,
                    })}
                  >
                    {submitError || submitErrors}
                  </p>
                )}

                <div className={css["spacer-bottom"]} />

                <button
                  className={classNames({
                    [css.submit]: true,
                    [css["is-loading"]]: submitting,
                  })}
                  type="submit"
                  disabled={pristine || hasValidationErrors}
                >
                  {t("continue")}
                </button>
              </form>
            )}
          </Form>

          <p className={css["terms-text"]}>
            {t("agree_to_terms")}{" "}
            <a href="/pages/terms" target="_blank" rel="noopener noreferrer">
              Terms & Services
            </a>
          </p>

          <p className={css["no-account-text"]}>
            {t("no_account")}{" "}
            <a
              className={classNames({
                [css["text-highlight"]]: true,
                [css["text-underline"]]: true,
              })}
              href="/sign_up"
              onClick={(e) => {
                e.preventDefault()
                onPageUpdate(Screens.SignUpScreen)
              }}
            >
              {t("join_now")}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
